import { useState } from 'react';
import { applyMask, toggleHiddenDigit, MASK_LIMITS, MAX_DISTINCT_VALUES } from '../utils/numberMask';
import { EyeOff, AlertCircle } from 'lucide-react';

/**
 * DigitMaskPicker — iter-226 (XYZ strict, POSITION order LTR, ≤3 distinct).
 *
 * Renders each digit of `value` as a tappable box. Tapping toggles hiding.
 * Rules enforced client-side (and mirrored on server):
 *   - At most 3 distinct digit values allowed (X, Y, Z).
 *   - Count 0..fullLen. If the number itself has ≤3 distinct digits, the
 *     merchant can hide them ALL.
 *   - Letter mapping = LEFT-TO-RIGHT position order in the number.
 * Shows an inline error when a rule is violated.
 */
export default function DigitMaskPicker({ value, hiddenIndices, onChange, kind, language, isDarkMode }) {
  const [error, setError] = useState(null);

  // iter-227d: safe-guards. `value` may be null/undefined during a partial edit;
  // returning null (instead of crashing on `.split`) prevents the whole route
  // tree from unmounting to the App loader.
  if (!value || typeof value !== 'string') return null;
  const limits = MASK_LIMITS[kind] || { max: 2, len: value.length };
  const fullLen = limits.len;
  const digits = value.split('');
  const safeHidden = Array.isArray(hiddenIndices) ? hiddenIndices : [];
  let masked;
  try {
    masked = applyMask(value, safeHidden);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[DigitMaskPicker] applyMask crashed', { value, safeHidden, e });
    masked = value;
  }
  const currentCount = safeHidden.length;

  const distinctCount = new Set(
    safeHidden.map(i => digits[i]).filter(Boolean)
  ).size;

  const handleToggle = (idx) => {
    const result = toggleHiddenDigit(safeHidden, idx, value, kind);
    if (result.error) {
      setError(result.error);
      setTimeout(() => setError(null), 3500);
      return;
    }
    setError(null);
    onChange(result.indices);
  };

  const handleHideAll = () => {
    if (value.length !== fullLen) return;
    // Check distinct-values cap for full-mask
    const distinctAll = new Set(digits);
    if (distinctAll.size > MAX_DISTINCT_VALUES) {
      setError({
        ar: 'لا يمكن إخفاء الرقم لعدم وجود تكرار أرقام زائد',
        en: 'Cannot hide all: too many distinct digit values',
      });
      setTimeout(() => setError(null), 3500);
      return;
    }
    setError(null);
    onChange(Array.from({ length: fullLen }, (_, i) => i));
  };

  const handleClearAll = () => {
    setError(null);
    onChange([]);
  };

  const showFullMaskButton = value.length === fullLen && currentCount !== fullLen;

  const capLabel = language === 'ar'
    ? `${currentCount} / ${fullLen}${currentCount === fullLen ? ' • كامل' : ''}`
    : `${currentCount} / ${fullLen}${currentCount === fullLen ? ' • all' : ''}`;

  return (
    <div
      className={`mt-3 p-3 rounded-xl border ${
        isDarkMode ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'
      }`}
      data-testid={`digit-mask-picker-${kind}`}
    >
      <div className="flex items-center justify-between mb-2 gap-2">
        <p className={`text-xs font-semibold ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>
          {language === 'ar' ? 'إخفاء بعض الأرقام (اختياري)' : 'Hide selected digits (optional)'}
        </p>
        <span className={`text-[10px] px-2 py-0.5 rounded-md ${
          isDarkMode ? 'bg-white/10 text-white/70' : 'bg-slate-200 text-slate-600'
        }`}>{capLabel}</span>
      </div>

      <p className={`text-[11px] mb-3 leading-relaxed ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
        {language === 'ar'
          ? `اضغط على الرقم لإخفائه. أي عدد حتى ${fullLen}. الأرقام المتماثلة تأخذ نفس الحرف. مسموح 3 قيم مختلفة كحد أقصى: X, Y, Z.`
          : `Tap to hide. Up to ${fullLen}. Same digits share a letter. Max 3 distinct values: X, Y, Z.`}
      </p>

      {/* Inline error */}
      {error && (
        <div className={`mb-3 flex items-start gap-2 text-[11px] px-3 py-2 rounded-lg ${
          isDarkMode ? 'bg-red-500/10 text-red-300 border border-red-500/30' : 'bg-red-50 text-red-700 border border-red-200'
        }`} data-testid={`digit-mask-error-${kind}`}>
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span>{language === 'ar' ? error.ar : error.en}</span>
        </div>
      )}

      {/* Digit tiles */}
      <div className="flex gap-1.5 justify-center flex-wrap" dir="ltr">
        {digits.map((d, idx) => {
          const hidden = safeHidden.includes(idx);
          const shownChar = hidden ? (masked[idx] || 'X') : d;
          return (
            <button
              key={idx}
              type="button"
              onClick={() => handleToggle(idx)}
              data-testid={`digit-mask-tile-${kind}-${idx}`}
              className={`w-9 h-11 rounded-lg font-mono font-bold text-lg border-2 transition-colors cursor-pointer
                ${hidden
                  ? 'bg-red-500/15 border-red-400 text-red-500'
                  : (isDarkMode
                      ? 'bg-white/5 border-white/15 text-white hover:border-cyan-400'
                      : 'bg-white border-slate-300 text-slate-800 hover:border-cyan-500')}`}
            >
              {shownChar}
            </button>
          );
        })}
      </div>

      {/* Shortcut buttons */}
      <div className="mt-3 flex items-center justify-center gap-2">
        {showFullMaskButton && (
          <button
            type="button"
            onClick={handleHideAll}
            data-testid={`digit-mask-hide-all-${kind}`}
            className={`text-[11px] px-2.5 py-1 rounded-md border transition
              ${isDarkMode
                ? 'bg-amber-500/10 border-amber-500/30 text-amber-300 hover:bg-amber-500/20'
                : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'}`}
          >
            {language === 'ar' ? `إخفاء الكل (${fullLen})` : `Hide all (${fullLen})`}
          </button>
        )}
        {currentCount > 0 && (
          <button
            type="button"
            onClick={handleClearAll}
            data-testid={`digit-mask-clear-${kind}`}
            className={`text-[11px] px-2.5 py-1 rounded-md border transition
              ${isDarkMode
                ? 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10'
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'}`}
          >
            {language === 'ar' ? 'إظهار الكل' : 'Clear'}
          </button>
        )}
      </div>

      {/* Live preview */}
      {currentCount > 0 && (
        <div className={`mt-3 flex items-center justify-center gap-2 text-xs ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
          <EyeOff className="h-3.5 w-3.5" />
          <span>{language === 'ar' ? 'سيظهر في المتجر:' : 'Displayed as:'}</span>
          <span className={`font-mono font-bold text-sm ${isDarkMode ? 'text-cyan-300' : 'text-cyan-700'}`}
                data-testid={`digit-mask-preview-${kind}`} dir="ltr">
            {masked}
          </span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-white/5 text-white/50' : 'bg-slate-200 text-slate-500'}`}>
            {distinctCount}/3
          </span>
        </div>
      )}
    </div>
  );
}
